// View 2: Service Available (Water availability confirmation)

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useOrderStore } from '@/stores/orderStore';
import { useFlowNavigation } from '@/hooks';
import { checkWaterServiceability } from '@/services/serviceabilityService';
import { skipsServiceAvailableScreen /*, shouldShowPropertyScreen */ } from '@/utils/serviceabilityFlow';

export function ServiceAvailableView() {
  const navigate = useNavigate();
  const { address, userId /*, propertyData */ } = useOrderStore();
  const { navigateFromProperty } = useFlowNavigation();

  // Double-click protection
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch water service data
  const { data: serviceResult } = useQuery({
    queryKey: ['waterService', address?.zip],
    queryFn: () => checkWaterServiceability(address!, userId || undefined),
    enabled: !!address?.zip,
  });

  const serviceData = serviceResult?.serviceData;
  const firstPlan = serviceData?.planData?.[0];

  const handleContinue = () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    // RETIRED (Sep 2026): RentCast property confirmation screen
    // if (shouldShowPropertyScreen(propertyData)) {
    //   navigate('/property');
    // } else {
    //   navigateFromProperty();
    // }
    // Use flow-aware navigation (goes to /personal-info for A, /utilities for B)
    navigateFromProperty();
  };

  // Vendors like Dallas, TX skip this screen entirely — if we land here
  // (refresh, back button), move straight along the order flow
  useEffect(() => {
    if (skipsServiceAvailableScreen(serviceData?.vendorName)) {
      // RETIRED (Sep 2026): RentCast property confirmation screen
      // if (shouldShowPropertyScreen(propertyData)) {
      //   navigate('/property', { replace: true });
      // } else {
      //   navigateFromProperty();
      // }
      navigateFromProperty();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceData?.vendorName]);

  const handleBack = () => {
    navigate('/');
  };

  // Build service details list from plan description or defaults
  const getServiceDetails = () => {
    // Try to parse from description or use defaults based on vendor
    const vendorName = serviceData?.vendorName?.toLowerCase() || ''
    if (vendorName.includes('dallas')) {
      return [
        'Residential Trash Bin',
        'Residential Sewer',
        'Residential Recycling',
        'Residential Garbage',
      ]
    }
    return [
      'Water service connection',
      'Sewer service (if applicable)',
      'Municipal utilities setup',
    ]
  }

  return (
    <section className="view" style={{ display: 'block' }}>
      <div className="container">
        <h1 className="text--center mb-6">Great news! Water service is available</h1>

        {/* Service Logo - only show if available */}
        {firstPlan?.logo && (
          <div className="text--center mb-6">
            <img
              src={firstPlan.logo}
              alt={serviceData?.vendorName || 'Water Service'}
              className="mx-auto"
              style={{ maxWidth: '120px', maxHeight: '80px' }}
            />
          </div>
        )}

        {/* Official Partner Card */}
        <div className="info-card info-card--beige">
          <div className="info-card__header">
            <img
              src="/img/icon/icon_check-badge.svg"
              alt=""
              className="info-card__icon info-card__icon--desktop"
            />
            <div className="info-card__content">
              <h2 className="info-card__title">
                <img
                  src="/img/icon/icon_check-badge-small.svg"
                  alt=""
                  className="info-card__icon info-card__icon--mobile me-2"
                />
                Official Partner
              </h2>
              <p className="info-card__description">
                2TurnItOn is the official partner of your city and has been contracted to process
                water service requests in your area.
              </p>
            </div>
          </div>
        </div>

        {/* Service Details Card */}
        <div className="info-card info-card--blue">
          <p className="info-card__section-title">SERVICE DETAILS</p>
          <p className="info-card__service-name">
            {serviceData?.name || 'Water Service'} - {serviceData?.vendorName || 'Local Provider'} - Water
          </p>
          <ul className="info-card__list">
            {getServiceDetails().map((detail, index) => (
              <li key={index}>{detail}</li>
            ))}
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="action-row">
          <button type="button" className="btn btn--back" onClick={handleBack}>
            GO BACK
          </button>
          <button
            type="button"
            className="btn"
            onClick={handleContinue}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'LOADING...' : 'CONTINUE'}
          </button>
        </div>
      </div>
    </section>
  )
}
